// Runtime apex derivation — ONE build serves both the primary g1.network and
// the registrar-independent backup apex g1network.dev. When served on a known
// platform apex we derive every API/auth base from window.location.hostname so
// the backup domain never sends traffic to a (possibly down) primary. This WINS
// over the build-time VITE_* env vars, which bake the primary at build time.
const PLATFORM_APEXES = ["g1.network", "g1network.dev"] as const;
function platformApex(): string | null {
	if (typeof window === "undefined") return null;
	const h = window.location.hostname;
	for (const a of PLATFORM_APEXES) if (h === a || h.endsWith(`.${a}`)) return a;
	return null;
}
const APEX = platformApex();

export const SHARED_API_URL = APEX
	? `https://api.${APEX}`
	: import.meta.env.VITE_SHARED_API_URL || "https://api.g1.network";
export const AUTH_URL = APEX
	? `https://auth.${APEX}`
	: import.meta.env.VITE_AUTH_URL || "https://auth.g1.network";
// media-api.g1.network is same-site with other g1.network subdomains,
// so SSO cookies work. The worker is also reachable at api.g1.reviews
// (different TLD) for public/external consumers.
let mediaApiBaseUrl = APEX
	? `https://media-api.${APEX}`
	: import.meta.env.VITE_MEDIA_API_URL || "https://media-api.g1.network";

export function setMediaApiUrl(url: string) {
	mediaApiBaseUrl = url;
}

export function getMediaApiUrl(): string {
	return mediaApiBaseUrl;
}

export class ApiError extends Error {
	constructor(
		public status: number,
		public statusText: string,
		public body: unknown,
	) {
		super(`API Error ${status}: ${statusText}`);
		this.name = "ApiError";
	}
}

export class NetworkError extends Error {
	constructor(message: string) {
		super(message);
		this.name = "NetworkError";
	}
}

function redirectToLogin(): never {
	const returnUrl = encodeURIComponent(window.location.href);
	window.location.replace(`${AUTH_URL}/?return=${returnUrl}`);
	throw new ApiError(401, "Unauthorized", null);
}

// Single in-flight refresh promise — share across concurrent 401s
let refreshPromise: Promise<boolean> | null = null;

async function tryRefresh(): Promise<boolean> {
	if (refreshPromise) return refreshPromise;
	refreshPromise = (async () => {
		try {
			// Phase A1b: refresh against auth.g1.network (canonical IdP).
			// Cookies are Domain=.g1.network so either host works during the
			// dual-serve window; new URL cuts over to the new path.
			const res = await fetch(`${AUTH_URL}/auth/refresh`, {
				method: "POST",
				credentials: "include",
				headers: { "Content-Type": "application/json" },
				body: "{}",
			});
			return res.ok;
		} catch {
			return false;
		} finally {
			setTimeout(() => {
				refreshPromise = null;
			}, 100);
		}
	})();
	return refreshPromise;
}

async function request<T>(baseUrl: string, path: string, options: RequestInit = {}): Promise<T> {
	const headers: Record<string, string> = {
		...((options.headers as Record<string, string>) ?? {}),
	};
	if (!(options.body instanceof FormData) && !headers["Content-Type"]) {
		headers["Content-Type"] = "application/json";
	}

	const doFetch = () =>
		fetch(`${baseUrl}${path}`, {
			...options,
			headers,
			credentials: "include",
		});

	let response: Response;
	try {
		response = await doFetch();
	} catch (_err) {
		throw new NetworkError("Network error — please check your connection and try again.");
	}

	// On 401 — try refresh once then retry (except for /auth/* endpoints)
	if (response.status === 401 && !path.startsWith("/auth/")) {
		const refreshed = await tryRefresh();
		if (refreshed) {
			try {
				response = await doFetch();
			} catch {
				redirectToLogin();
			}
		}
		if (response.status === 401) {
			redirectToLogin();
		}
	}

	if (!response.ok) {
		const body = await response.json().catch(() => null);
		throw new ApiError(response.status, response.statusText, body);
	}

	if (response.status === 204) {
		return undefined as T;
	}

	return response.json() as Promise<T>;
}

/** Extract a user-friendly message from an API or network error. */
export function friendlyErrorMessage(err: unknown): string {
	if (err instanceof NetworkError) {
		return err.message;
	}
	if (err instanceof ApiError) {
		const body = err.body as { error?: string | Record<string, string[]> } | null;
		if (typeof body?.error === "string") return body.error;
		if (body?.error && typeof body.error === "object") {
			// Zod field errors — flatten to readable string
			const messages = Object.entries(body.error)
				.map(([field, msgs]) => `${field}: ${(msgs as string[]).join(", ")}`)
				.join("; ");
			return messages || err.message;
		}
		return err.message;
	}
	if (err instanceof Error) {
		return err.message;
	}
	return "An unexpected error occurred.";
}

export const sharedApi = {
	get: <T>(path: string) => request<T>(SHARED_API_URL, path),
	post: <T>(path: string, body: unknown) =>
		request<T>(SHARED_API_URL, path, {
			method: "POST",
			body: JSON.stringify(body),
		}),
	put: <T>(path: string, body: unknown) =>
		request<T>(SHARED_API_URL, path, {
			method: "PUT",
			body: JSON.stringify(body),
		}),
	patch: <T>(path: string, body: unknown) =>
		request<T>(SHARED_API_URL, path, {
			method: "PATCH",
			body: JSON.stringify(body),
		}),
	delete: <T>(path: string) => request<T>(SHARED_API_URL, path, { method: "DELETE" }),
	postFormData: <T>(path: string, body: FormData) =>
		request<T>(SHARED_API_URL, path, {
			method: "POST",
			body,
		}),
};

export const mediaApi = {
	get: <T>(path: string) => request<T>(mediaApiBaseUrl, path),
	post: <T>(path: string, body: unknown) =>
		request<T>(mediaApiBaseUrl, path, {
			method: "POST",
			body: JSON.stringify(body),
		}),
	put: <T>(path: string, body: unknown) =>
		request<T>(mediaApiBaseUrl, path, {
			method: "PUT",
			body: JSON.stringify(body),
		}),
	patch: <T>(path: string, body: unknown) =>
		request<T>(mediaApiBaseUrl, path, {
			method: "PATCH",
			body: JSON.stringify(body),
		}),
	delete: <T>(path: string) => request<T>(mediaApiBaseUrl, path, { method: "DELETE" }),
	postFormData: <T>(path: string, body: FormData) =>
		request<T>(mediaApiBaseUrl, path, {
			method: "POST",
			body,
		}),
};

const TRAFFIC_API_URL = APEX ? `https://traffic.${APEX}` : "https://traffic.g1.network";

export const trafficApi = {
	get: <T>(path: string) => request<T>(TRAFFIC_API_URL, path),
	post: <T>(path: string, body: unknown) =>
		request<T>(TRAFFIC_API_URL, path, {
			method: "POST",
			body: JSON.stringify(body),
		}),
	put: <T>(path: string, body: unknown) =>
		request<T>(TRAFFIC_API_URL, path, {
			method: "PUT",
			body: JSON.stringify(body),
		}),
	patch: <T>(path: string, body: unknown) =>
		request<T>(TRAFFIC_API_URL, path, {
			method: "PATCH",
			body: JSON.stringify(body),
		}),
	delete: <T>(path: string) => request<T>(TRAFFIC_API_URL, path, { method: "DELETE" }),
	postFormData: <T>(path: string, body: FormData) =>
		request<T>(TRAFFIC_API_URL, path, {
			method: "POST",
			body,
		}),
};

/** Logout — clears SSO cookies on .g1.network then redirects to auth page. */
export async function logout(): Promise<void> {
	try {
		await fetch(`${AUTH_URL}/auth/logout`, {
			method: "POST",
			credentials: "include",
		});
	} catch {
		// ignore
	}
	const returnUrl = encodeURIComponent(`${window.location.origin}/`);
	window.location.replace(`${AUTH_URL}/?return=${returnUrl}`);
}
